import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Stack, TextField, IconButton, Link, Menu, MenuItem,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FlagIcon from '@mui/icons-material/Flag';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import sendRequest from '../utils/sendRequest';
import LargeTooltip from '../styled/CustomTooltip';

const getRelativeTime = (timestamp) => {
  const elapsed = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(elapsed / (1000 * 60));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? 's' : ''} ago`;
};

const linkify = (text) => {
  const urlRegex = /\b((?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z]{2,})(?:[^\s"<>]*)?)/gi;
  return text.replace(urlRegex, (match) => {
    let href = match;
    if (!/^https?:\/\//i.test(href)) href = 'http://' + href;
    const escapedHref = href.replace(/"/g, '&quot;');
    return `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer">${match}</a>`;
  });
};

const nestComments = (comments, replies) => {
  const map = {};
  const roots = [];
  comments.forEach(c => map[c.id] = { ...c, replies: [] });
  replies.forEach(r => map[r.id] = { ...r, replies: [] });
  replies.forEach(r => { if (map[r.parent]) map[r.parent].replies.push(map[r.id]); });
  comments.forEach(c => roots.push(map[c.id]));
  return roots;
};

// Moved above to prevent hoisting error
const Comment = ({
  comment,
  onReplyClick,
  replyText,
  onReplyChange,
  onReplySubmit,
  onCancelReply,
  collapsed,
  toggleCollapse,
  depth = 0,
  isVisible = true,
  isLastReply = false,
  addSnackbar,
  poDocId,
  setFlagResolutionTarget,
  fetchPoComments,
  actionsAllowed,
  resolvedId,
}) => {
  const isReply = depth > 0;
  const inputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleMenuClose = () => setMenuOpen(false);

  const handleFlagComment = async () => {
    handleMenuClose();
    try {
      await sendRequest(
        `purchase-orders/flag-comment?po_doc_id=${poDocId}&comment_id=${comment.id}`,
        {},
        'POST'
      );
      addSnackbar('Comment flagged successfully!');
      await fetchPoComments();
    } catch (error) {
      addSnackbar('Failed to flag comment.', false);
    }
  };

  const handleResolveFlag = () => {
    onReplyClick(comment.id);
    setFlagResolutionTarget(comment.id);
    handleMenuClose();
  };

  useEffect(() => {
    if (replyText[comment.id] !== undefined && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyText, comment.id]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onReplySubmit(comment.id);
    }
  };

  const showFlag = !isReply && comment.flagged;
  const flagColor = showFlag ? (comment.resolved ? 'success.main' : 'error.main') : null;
  const isResolutionReply = comment.id === resolvedId;

  return (
    <Box sx={{ mt: isReply ? 0 : 2 }}>
      <Box sx={{ position: 'relative', pl: isReply ? 1 : 0 }}>
        {isReply && (
          <Box
            sx={{
              content: '""',
              position: 'absolute',
              left: isLastReply ? -12 : -14,
              width: 16,
              height: isLastReply ? 25 : 16,
              borderLeft: '2px solid #bbb',
              borderBottom: '2px solid #bbb',
              borderBottomLeftRadius: 10,
              pointerEvents: 'none',
            }}
          />
        )}
        {isLastReply && (<Typography fontSize={'5px'}>&nbsp;</Typography>)}
        <Typography
          variant="body2"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowWrap: 'break-word', wordBreak: 'break-word' }}
        >
          <strong>{comment.user}</strong> • {getRelativeTime(comment.time)}
          {showFlag && <FlagIcon fontSize="small" sx={{ color: flagColor }} />}
          {isResolutionReply && <FlagIcon fontSize="small" sx={{ color: 'success.main' }} />}
          {actionsAllowed &&
            <LargeTooltip title={`Reply to ${comment.user}`} arrow>
              <IconButton size="small" onClick={() => onReplyClick(comment.id)} sx={{ p: 0.5 }}>
                <ReplyIcon fontSize="small" />
              </IconButton>
            </LargeTooltip>
          }
          {actionsAllowed && depth === 0 && !comment.resolved && (
            <>
              <IconButton size="small" onClick={handleMenuClick} sx={{ p: 0.5 }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} PaperProps={{ sx: { width: '200px' } }}>
                {comment.flagged
                  ? <MenuItem onClick={handleResolveFlag}>Resolve Flag</MenuItem>
                  : <MenuItem onClick={handleFlagComment}>Flag Comment</MenuItem>}
              </Menu>
            </>
          )}
          {comment.replies.length > 0 && depth === 0 && (
            <IconButton size="small" onClick={() => toggleCollapse(comment.id)} sx={{ p: 0.5 }}>
              {collapsed[comment.id]
                ? <LargeTooltip title="Show Replies" arrow><ExpandMoreIcon fontSize="small" /></LargeTooltip>
                : <LargeTooltip title="Hide Replies" arrow><ExpandLessIcon fontSize="small" /></LargeTooltip>}
            </IconButton>
          )}
        </Typography>
      </Box>

      <Typography
        variant="body1"
        sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word', ml: isReply ? 1 : 0 }}
        dangerouslySetInnerHTML={{ __html: linkify(comment.comment || comment.reply) }}
      />

      {replyText[comment.id] !== undefined && (
        <Box sx={{ mt: 1 }}>
          <TextField
            inputRef={inputRef}
            multiline
            minRows={2}
            variant="outlined"
            fullWidth
            placeholder="Write a reply..."
            value={replyText[comment.id]}
            onChange={(e) => onReplyChange(comment.id, e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <LargeTooltip title="Send Reply" arrow>
              <IconButton onClick={() => onReplySubmit(comment.id)} color="primary"><SendIcon /></IconButton>
            </LargeTooltip>
            <LargeTooltip title="Cancel Reply" arrow>
              <IconButton onClick={() => onCancelReply(comment.id)}>✖</IconButton>
            </LargeTooltip>
          </Stack>
        </Box>
      )}

      {!collapsed[comment.id] && comment.replies.length > 0 && (
        <Box>
          {comment.replies.map((reply, index) => (
            <Box
              key={reply.id}
              sx={{
                borderLeft: index < comment.replies.length - 1 ? '2px solid #bbb' : 'none',
                ml: 1.5,
                pl: 1.5,
                mt: index < comment.replies.length - 1 ? 2 : 0,
              }}
            >
              <Comment
                comment={reply}
                onReplyClick={onReplyClick}
                replyText={replyText}
                onReplyChange={onReplyChange}
                onReplySubmit={onReplySubmit}
                onCancelReply={onCancelReply}
                collapsed={collapsed}
                toggleCollapse={toggleCollapse}
                depth={depth + 1}
                isVisible={isVisible}
                isLastReply={index === comment.replies.length - 1}
                addSnackbar={addSnackbar}
                poDocId={poDocId}
                setFlagResolutionTarget={setFlagResolutionTarget}
                fetchPoComments={fetchPoComments}
                actionsAllowed={actionsAllowed}
                resolvedId={comment.resolved}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const PurchaseOrderComments = ({ addSnackbar, setLoading }) => {
  const { id } = useParams();
  const [comments, setComments] = useState(null);
  const [actionsAllowed, setActionsAllowed] = useState(false);
  const [replies, setReplies] = useState([]);
  const [poDocId, setPoDocId] = useState('');
  const [poName, setPoName] = useState('');
  const [newComment, setNewComment] = useState('');
  const [replyText, setReplyText] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [flagResolutionTarget, setFlagResolutionTarget] = useState(null);
  const scrollRef = useRef(null);

  const fetchPoComments = useCallback(async () => {
    setLoading(true);
    try {
      const dashData = await sendRequest('purchase-orders');
      const map = dashData.po_list.reduce((acc, po) => {
        acc[po.id] = po.doc_id;
        return acc;
      }, {});
      const po_doc_id = map[id];
      setPoDocId(po_doc_id);
      if (!po_doc_id) {
        addSnackbar('Invalid Purchase Order ID.', false);
        return;
      }
      const data = await sendRequest(`purchase-orders/details?po_doc_id=${po_doc_id}`);
      setPoName(data.name);
      setComments(data.comments);
      setReplies(data.replies);
      setActionsAllowed(data.comments_allowed);
    } catch (error) {
      addSnackbar(error.message, false);
    } finally {
      setLoading(false);
    }
  }, [id, addSnackbar, setLoading]);

  const addPoComment = useCallback(async (commentText, replyTo = null, isFlagResolution = false) => {
    try {
      let response;
      if (replyTo) {
        const endpoint = isFlagResolution
          ? `purchase-orders/add-flag-resolution-reply?po_doc_id=${poDocId}`
          : `purchase-orders/add-reply?po_doc_id=${poDocId}`;
        response = await sendRequest(endpoint, { parent: replyTo, reply: commentText }, 'POST');
      } else {
        response = await sendRequest(
          `purchase-orders/add-comment?po_doc_id=${poDocId}`,
          { flagged: false, comment: commentText },
          'POST'
        );
      }
      addSnackbar(response.message);
      await fetchPoComments();
    } catch (error) {
      addSnackbar(error.message, false);
    }
  }, [poDocId, fetchPoComments, addSnackbar]);

  const handleReplyChange = (commentId, value) => {
    setReplyText(prev => ({ ...prev, [commentId]: value }));
  };

  const handleReplySubmit = (parentId) => {
    const text = replyText[parentId]?.trim();
    if (text) {
      addPoComment(text, parentId, parentId === flagResolutionTarget);
      setReplyText(prev => {
        const newState = { ...prev };
        delete newState[parentId];
        return newState;
      });
      if (parentId === flagResolutionTarget) {
        setFlagResolutionTarget(null);
      }
    }
  };

  const handleCancelReply = (commentId) => {
    setReplyText(prev => {
      const newState = { ...prev };
      delete newState[commentId];
      return newState;
    });
  };

  const handleTopLevelAdd = () => {
    if (newComment.trim()) {
      addPoComment(newComment);
      setNewComment('');
    }
  };

  const handleNewCommentKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTopLevelAdd();
    }
  };

  const toggleCollapse = (commentId) => {
    setCollapsed(prev => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  useEffect(() => {
    fetchPoComments();
  }, [fetchPoComments]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments, replies]);

  if (!comments) return <Typography>Loading...</Typography>;

  const structuredComments = nestComments(comments, replies);

  return (
    <Box sx={{ px: 2, pt: 2, pb: 8, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Link href={`/purchase-orders/${id}`} underline="hover" sx={{ display: 'block', mb: 2 }}>
        ← Back to PO Details
      </Link>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Conversations for {`${poName} (${id})`}</Typography>
      </Box>

      <Box sx={{ borderTop: '1px solid #aaa', mt: 2 }} />

      <Box ref={scrollRef} sx={{ maxHeight: '75vh', overflowY: 'auto', pr: 1, pb: 12 }}>
        {structuredComments.length === 0 ? (
          <Typography>No comments yet.</Typography>
        ) : (
          structuredComments.map(comment => (
            <Box key={comment.id} sx={{ borderBottom: '1px solid #ddd', mb: 2 }}>
              <Comment
                comment={comment}
                onReplyClick={(id) => setReplyText(prev => ({ ...prev, [id]: '' }))}
                replyText={replyText}
                onReplyChange={handleReplyChange}
                onReplySubmit={handleReplySubmit}
                onCancelReply={handleCancelReply}
                collapsed={collapsed}
                toggleCollapse={toggleCollapse}
                depth={0}
                isVisible
                addSnackbar={addSnackbar}
                poDocId={poDocId}
                setFlagResolutionTarget={setFlagResolutionTarget}
                fetchPoComments={fetchPoComments}
                actionsAllowed={actionsAllowed}
              />
            </Box>
          ))
        )}
      </Box>

      {actionsAllowed &&
        <Box sx={{ position: 'sticky', bottom: 0, bgcolor: '#f8f9fa', pt: 1, pb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              multiline
              minRows={2}
              variant="outlined"
              fullWidth
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleNewCommentKeyDown}
            />
            <IconButton onClick={handleTopLevelAdd} sx={{ mt: 0.5 }} color="primary">
              <SendIcon />
            </IconButton>
          </Stack>
        </Box>
      }
    </Box>
  );
};

export default PurchaseOrderComments;
